import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { Eye, EyeOff, LogIn, UserPlus, KeyRound } from "lucide-react";
import api from "../lib/api";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFirstUser, setIsFirstUser] = useState(false);
  const [checkingFirstUser, setCheckingFirstUser] = useState(true);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    checkFirstUser();
  }, []);

  const checkFirstUser = async () => {
    try {
      const response = await api.get("/auth/check-first-user");
      setIsFirstUser(response.data.is_first_user);
    } catch (error) {
      console.error("Error checking first user:", error);
    } finally {
      setCheckingFirstUser(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success("Login realizado com sucesso!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao fazer login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md animate-fade-in">
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl font-bold text-primary mb-2">
              EstoqueClinic
            </h1>
            <p className="text-slate-500">
              Sistema de Gestão de Estoque para Clínicas de Estética
            </p>
          </div>

          {checkingFirstUser ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : isFirstUser ? (
            /* Primeiro acesso - Criar Admin */
            <Card className="border-slate-200 shadow-soft border-primary/50">
              <CardHeader className="space-y-1">
                <div className="flex items-center gap-2">
                  <UserPlus className="h-6 w-6 text-primary" />
                  <CardTitle className="text-2xl font-heading">Primeiro Acesso</CardTitle>
                </div>
                <CardDescription>
                  Bem-vindo! Crie sua conta de <strong>Administrador</strong> para começar a usar o sistema.
                </CardDescription>
              </CardHeader>
              <CardFooter className="flex flex-col gap-4">
                <Link to="/checkout" className="w-full">
                  <Button className="w-full h-11" data-testid="create-admin-btn">
                    <UserPlus className="mr-2 h-5 w-5" />
                    Comprar e Criar Conta
                  </Button>
                </Link>
                <p className="text-xs text-center text-slate-500">
                  Acesso vitalicio por apenas R$ 19,90. Voce sera o administrador do sistema.
                </p>
              </CardFooter>
            </Card>
          ) : (
            /* Login normal */
            <Card className="border-slate-200 shadow-soft">
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl font-heading">Entrar</CardTitle>
                <CardDescription>
                  Entre com suas credenciais para acessar o sistema
                </CardDescription>
              </CardHeader>
              <form onSubmit={handleSubmit}>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">E-mail</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="login-email-input"
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Senha</Label>
                      <Link
                        to="/forgot-password"
                        className="text-sm text-primary hover:text-primary/80 hover:underline"
                        data-testid="forgot-password-link"
                      >
                        Esqueci a senha
                      </Link>
                    </div>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        data-testid="login-password-input"
                        className="h-11 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        data-testid="toggle-password-visibility"
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex flex-col gap-4">
                  <Button
                    type="submit"
                    className="w-full h-11"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    ) : (
                      <>
                        <LogIn className="mr-2 h-5 w-5" />
                        Entrar
                      </>
                    )}
                  </Button>
                  
                  <div className="relative w-full">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-slate-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-slate-500">ou</span>
                    </div>
                  </div>

                  <Link to="/checkout" className="w-full">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-11"
                      data-testid="register-link-btn"
                    >
                      <UserPlus className="mr-2 h-5 w-5" />
                      Criar Nova Conta
                    </Button>
                  </Link>

                  <p className="text-xs text-center text-slate-500">
                    Acesso vitalicio por apenas R$ 29,90
                  </p>
                </CardFooter>
              </form>
            </Card>
          )}
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:flex flex-1 relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1669930605340-801a0be1f5a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxhZXN0aGV0aWMlMjBjbGluaWMlMjBpbnRlcmlvciUyMGJyaWdodCUyMGNsZWFufGVufDB8fHx8MTc2NzgwODk2MHww&ixlib=rb-4.1.0&q=85')",
          }}
        />
        <div className="absolute inset-0 bg-primary/60" />
        <div className="relative z-10 flex flex-col items-center justify-center p-12 text-white">
          <h2 className="font-heading text-4xl font-bold mb-4 text-center">
            Controle Total do Seu Estoque
          </h2>
          <p className="text-lg text-center text-white/90 max-w-md">
            Gerencie produtos, fornecedores, entradas e saídas com facilidade.
            Receba alertas de vencimento e estoque baixo em tempo real.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
